// WorkspacesPage — /workspaces: every workspace you own or that is shared with you, one
// card each. Owners manage state, usage, members and deletion; members and viewers see the
// state and the links. Hosting (awake time, storage) and AI are charged from the account's
// prepaid credit (/billing); the Usage tab shows what this workspace used.

import { h, Component, Fragment } from 'preact';
import { route } from 'preact-router';
import {
  listWorkspaces, createWorkspace, workspaceState, renameWorkspace, deleteWorkspace, wakeWs, sleepWs,
  keepAwakeWs, usageWs, listMembers, inviteMember, setMemberRole, removeMember,
} from '../api.js';
import { Orb } from './orb.js';
import { Icon } from './icons.js';
import { orbFor, stateLabel } from './switcher.js';
import { Modal } from './dialogs.js';
import { ActionCard } from './billing.js';
import './ui.css';

const eur = (n) => (n == null ? '—' : '€' + Number(n).toFixed(2));
const hours = (n) => (n == null ? '—' : `${Number(n).toFixed(1)} h`);
const monthName = (m) => { try { const [y, mo] = m.split('-'); return new Date(+y, +mo - 1, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' }); } catch (e) { return m; } };

class Usage extends Component {
  constructor(p) { super(p); this.state = { u: null, error: '' }; }
  componentDidMount() { this.load(); }
  async load() { try { this.setState({ u: await usageWs(this.props.ws.id) }); } catch (e) { this.setState({ error: e.message }); } }
  render(_, { u, error }) {
    if (error) return h('p', { className: 'note err' }, error);
    if (!u) return h('div', { className: 'pv-loading' }, h('span', { className: 'spin' }), ' Loading usage…');
    const m = u.month || {}; const est = m.estimate || {}; const r = u.rates || {};
    return h('div', { className: 'usage' },
      h('div', { className: 'stat-row' },
        h('div', { className: 'stat' }, h('small', null, 'Storage now'), h('b', null, `${(u.storage_gb || 0).toFixed(2)} GB`)),
        h('div', { className: 'stat' }, h('small', null, 'Awake this month'), h('b', null, hours(m.awake_hours))),
        h('div', { className: 'stat' }, h('small', null, 'AI this month'), h('b', null, eur(m.ai_spend_eur))),
        h('div', { className: 'stat total' }, h('small', null, 'Cost this month'), h('b', null, eur(est.total_eur)))),
      h('p', { className: 'muted small' },
        `At ${eur(r.storage_gb_month)} per GB-month and ${eur(r.awake_hour)} per awake hour, from your hosting credit; AI from your AI credit. `,
        h('a', { href: '/billing' }, 'Credit and activity')),
      (u.history || []).length ? h('details', { className: 'hist' }, h('summary', null, 'Earlier months'),
        h('table', { className: 'mini' }, h('thead', null, h('tr', null, h('th', null, 'Month'), h('th', null, 'Awake'), h('th', null, 'Storage'), h('th', null, 'AI'), h('th', null, 'Cost'))),
          h('tbody', null, u.history.map((x) => h('tr', { key: x.month },
            h('td', null, monthName(x.month)), h('td', null, hours(x.awake_hours)),
            h('td', null, `${(x.storage_gb_days / (x.days_in_month || 30)).toFixed(2)} GB avg`),
            h('td', null, eur(x.ai_spend_eur)), h('td', null, eur((x.estimate || {}).total_eur))))))) : null);
  }
}

class Members extends Component {
  constructor(p) { super(p); this.state = { list: null, email: '', role: 'viewer', busy: false, error: '', note: '' }; }
  componentDidMount() { this.load(); }
  async load() { try { this.setState({ list: await listMembers(this.props.ws.id) }); } catch (e) { this.setState({ error: e.message }); } }
  async invite(e) {
    e.preventDefault();
    const email = this.state.email.trim(); if (!email) return;
    this.setState({ busy: true, error: '', note: '' });
    try {
      const r = await inviteMember(this.props.ws.id, email, this.state.role);
      this.setState({ list: r.members, email: '', busy: false,
        note: r.invited && r.invited.status === 'invited' ? `Invitation sent to ${email}.` : `${email} now has access.` });
    } catch (err) { this.setState({ busy: false, error: err.message }); }
  }
  async setRole(m, role) { try { this.setState({ list: await setMemberRole(this.props.ws.id, m.id, role) }); } catch (e) { this.setState({ error: e.message }); } }
  async remove(m) {
    if (!window.confirm(`Remove ${m.email} from “${this.props.ws.name}”?`)) return;
    try { this.setState({ list: await removeMember(this.props.ws.id, m.id) }); } catch (e) { this.setState({ error: e.message }); }
  }
  render(_, s) {
    return h('div', { className: 'members' },
      h('ul', { className: 'people' }, (s.list || []).map((m) => h('li', { key: m.id || 'owner' },
        h('span', { className: 'avatar', 'aria-hidden': 'true' }, (m.name || m.email || '?').slice(0, 1).toUpperCase()),
        h('span', { className: 'grow' }, m.name || m.email, h('small', null, m.name ? m.email : (m.status === 'invited' ? 'Invitation pending' : ''))),
        m.role === 'owner' ? h('span', { className: 'role-badge' }, 'Owner')
          : h(Fragment, null,
            h('select', { value: m.role, 'aria-label': `Role of ${m.email}`, onChange: (e) => this.setRole(m, e.target.value) },
              h('option', { value: 'member' }, 'Member'), h('option', { value: 'viewer' }, 'Viewer')),
            h('button', { type: 'button', className: 'icon-btn', 'aria-label': `Remove ${m.email}`, onClick: () => this.remove(m) }, h(Icon, { name: 'close' })))))),
      h('form', { className: 'copy-row', onSubmit: (e) => this.invite(e) },
        h('input', { type: 'email', placeholder: 'Invite by email', value: s.email, 'aria-label': 'Email to invite', onInput: (e) => this.setState({ email: e.target.value }) }),
        h('select', { value: s.role, 'aria-label': 'Role', onChange: (e) => this.setState({ role: e.target.value }) },
          h('option', { value: 'viewer' }, 'Viewer'), h('option', { value: 'member' }, 'Member')),
        h('button', { type: 'submit', className: 'btn primary', disabled: s.busy }, s.busy ? 'Inviting…' : 'Invite')),
      h('p', { className: 'muted small' }, 'Viewers read chats and files. Members also chat with the agent and change files. You pay for what everyone uses.'),
      s.note ? h('p', { className: 'note ok' }, s.note) : null,
      s.error ? h('p', { className: 'note err' }, s.error) : null);
  }
}

class Card extends Component {
  constructor(p) { super(p); this.state = { tab: 'overview', editing: false, name: p.ws.name, busy: '', error: '', confirmDelete: false, typed: '' }; }
  async act(label, fn) {
    this.setState({ busy: label, error: '' });
    try { const ws = await fn(); if (ws && ws.id) this.props.onChange(ws); }
    catch (e) { this.setState({ error: e.code ? '' : e.message, action: e.code || null }); }
    this.setState({ busy: '' });
  }
  async saveName(e) {
    e.preventDefault();
    const name = this.state.name.trim();
    this.setState({ editing: false });
    if (name && name !== this.props.ws.name) await this.act('rename', () => renameWorkspace(this.props.ws.id, name));
  }
  async del() {
    this.setState({ busy: 'delete', error: '' });
    try { await deleteWorkspace(this.props.ws.id); this.props.onDeleted(this.props.ws.id); }
    catch (e) { this.setState({ busy: '', error: e.message }); }
  }
  render({ ws, current, onOpen }, s) {
    const owner = ws.role === 'owner';
    const setting = ['PENDING', 'PROVISIONING'].includes(ws.status);
    const url = ws.url || '';
    const tabs = owner ? [['overview', 'Overview'], ['usage', 'Usage'], ['members', 'People']] : [['overview', 'Overview']];
    return h('article', { className: 'ws-card' + (ws.id === current ? ' current' : '') },
      h('header', { className: 'ws-card-head' },
        h(Orb, { state: orbFor(ws), size: 34 }),
        h('div', { className: 'grow' },
          s.editing
            ? h('form', { onSubmit: (e) => this.saveName(e) }, h('input', { className: 'name-input', value: s.name, maxLength: 64, 'aria-label': 'Workspace name',
              ref: (el) => el && el.focus(), onInput: (e) => this.setState({ name: e.target.value }), onBlur: (e) => this.saveName(e) }))
            : h('h2', null, ws.name || 'Workspace',
              owner ? h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Rename', title: 'Rename', onClick: () => this.setState({ editing: true, name: ws.name }) }, h(Icon, { name: 'pencil', size: 14 })) : null),
          h('p', { className: 'muted small' }, stateLabel(ws), owner ? '' : ` · ${ws.role === 'member' ? 'Member' : 'Viewer'}${ws.owner_email ? ' · shared by ' + ws.owner_email : ''}`,
            ws.keep_awake ? ' · kept awake 24/7' : '')),
        h('button', { type: 'button', className: 'btn primary', onClick: () => onOpen(ws.id) }, h(Icon, { name: 'send', size: 14 }), ' Open chats')),
      tabs.length > 1 ? h('div', { className: 'tabs seg' }, tabs.map(([k, label]) =>
        h('button', { key: k, type: 'button', className: 'tab' + (s.tab === k ? ' on' : ''), onClick: () => this.setState({ tab: k }) }, label))) : null,
      s.tab === 'overview' ? h('div', { className: 'ws-card-body' },
        h('div', { className: 'btn-row' },
          ws.sleep_state !== 'AWAKE' || setting
            ? h('button', { type: 'button', className: 'btn', disabled: !!s.busy || setting, onClick: () => this.act('wake', () => wakeWs(ws.id)) }, s.busy === 'wake' ? 'Waking…' : 'Wake')
            : owner ? h('button', { type: 'button', className: 'btn', disabled: !!s.busy, onClick: () => this.act('sleep', () => sleepWs(ws.id)) }, s.busy === 'sleep' ? 'Stopping…' : 'Put to sleep') : null,
          owner ? h('label', { className: 'switch' },
            h('input', { type: 'checkbox', checked: !!ws.keep_awake, disabled: !!s.busy, onChange: (e) => this.act('keep', () => keepAwakeWs(ws.id, e.target.checked)) }),
            h('span', null, 'Keep awake 24/7')) : null),
        url && !setting ? h('div', { className: 'links' },
          h('a', { href: url, target: '_blank', rel: 'noopener' }, h(Icon, { name: 'external', size: 14 }), ' App'),
          h('a', { href: url + '/vscode/', target: '_blank', rel: 'noopener' }, h(Icon, { name: 'code', size: 14 }), ' Editor'),
          h('a', { href: url + '/term/', target: '_blank', rel: 'noopener' }, h(Icon, { name: 'shell', size: 14 }), ' Terminal')) : null,
        h('p', { className: 'muted small' }, setting ? 'Setting up the workspace. This takes a minute or two.'
          : 'Asleep, a workspace costs storage only; it wakes when someone sends a message.'),
        owner ? h('div', { className: 'danger-zone' },
          h('button', { type: 'button', className: 'btn ghost danger-text', onClick: () => this.setState({ confirmDelete: true, typed: '' }) }, h(Icon, { name: 'archive', size: 14 }), ' Delete workspace')) : null)
      : s.tab === 'usage' ? h('div', { className: 'ws-card-body' }, h(Usage, { ws }))
      : h('div', { className: 'ws-card-body' }, h(Members, { ws })),
      s.action ? h(ActionCard, { code: s.action, compact: true }) : null,
      s.error ? h('p', { className: 'note err' }, s.error) : null,
      h(Modal, { open: s.confirmDelete, title: `Delete “${ws.name}”?`, onClose: () => this.setState({ confirmDelete: false }) },
        h('p', { className: 'muted-p' }, 'This erases the workspace’s volume: every file, the running app and its settings. It can’t be undone. Its chats stay readable in the archive, and anyone it was shared with loses access.'),
        h('label', { className: 'field' }, h('span', null, `Type ${ws.name} to confirm`),
          h('input', { value: s.typed, onInput: (e) => this.setState({ typed: e.target.value }), 'aria-label': 'Workspace name to confirm' })),
        h('div', { className: 'modal-actions' },
          h('button', { type: 'button', className: 'btn ghost', onClick: () => this.setState({ confirmDelete: false }) }, 'Cancel'),
          h('button', { type: 'button', className: 'btn danger', disabled: s.typed.trim() !== (ws.name || '').trim() || s.busy === 'delete', onClick: () => this.del() },
            s.busy === 'delete' ? 'Deleting…' : 'Delete workspace'))));
  }
}

export class WorkspacesPage extends Component {
  constructor(p) { super(p); this.state = { list: null, current: null, max: 3, creating: false, name: '', busy: false, error: '' }; this.timer = null; }
  componentDidMount() { this.load(); this.timer = setInterval(() => this.poll(), 8000); }
  componentWillUnmount() { clearInterval(this.timer); }
  async load() {
    try {
      const r = await listWorkspaces();
      this.setState({ list: r.workspaces, current: r.current, error: '' });
      if (this.props.onWsState && r.current) {
        const cur = r.workspaces.find((w) => w.id === r.current);
        if (cur) this.props.onWsState(cur.sleep_state, cur.name);
      }
    } catch (e) { this.setState({ error: e.message }); }
  }
  async poll() {
    const busy = (this.state.list || []).filter((w) => ['PENDING', 'PROVISIONING'].includes(w.status) || w.sleep_state === 'WAKING');
    for (const w of busy) {
      try { this.update(await workspaceState(w.id)); } catch (e) { /* next round */ }
    }
  }
  update(ws) { this.setState((s) => ({ list: (s.list || []).map((w) => (w.id === ws.id ? { ...w, ...ws, role: w.role, owner_email: w.owner_email } : w)) })); }
  async create(e) {
    e.preventDefault();
    this.setState({ busy: true, error: '' });
    try {
      const ws = await createWorkspace(this.state.name.trim() || 'Workspace');
      this.setState((s) => ({ list: [{ ...ws, role: 'owner' }, ...(s.list || [])], creating: false, name: '', busy: false, current: ws.id }));
    } catch (err) { this.setState({ busy: false, error: err.code ? '' : err.message, action: err.code || null }); }
  }
  open(id) {
    try { localStorage.setItem('uai.workspace', String(id)); } catch (e) { /* */ }
    route('/?workspace=' + id);
  }
  render(_, s) {
    const own = (s.list || []).filter((w) => w.role === 'owner');
    const shared = (s.list || []).filter((w) => w.role !== 'owner');
    return h('div', { className: 'ws-page' },
      h('div', { className: 'ws-page-head' },
        h('div', null, h('h1', null, 'Workspaces'),
          h('p', { className: 'muted' }, 'Each workspace is its own computer: files, running apps and the agent’s memory. Each is billed separately.')),
        s.creating
          ? h('form', { className: 'copy-row', onSubmit: (e) => this.create(e) },
            h('input', { value: s.name, placeholder: 'Name, e.g. Client site', maxLength: 64, 'aria-label': 'New workspace name', ref: (el) => el && el.focus(), onInput: (e) => this.setState({ name: e.target.value }) }),
            h('button', { type: 'submit', className: 'btn primary', disabled: s.busy }, s.busy ? 'Creating…' : 'Create'),
            h('button', { type: 'button', className: 'btn ghost', onClick: () => this.setState({ creating: false }) }, 'Cancel'))
          : h('button', { type: 'button', className: 'btn primary', disabled: own.length >= s.max, title: own.length >= s.max ? `You can have up to ${s.max} workspaces` : '', onClick: () => this.setState({ creating: true }) },
            h(Icon, { name: 'plus', size: 14 }), ' New workspace')),
      s.action ? h(ActionCard, { code: s.action }) : null,
      s.error ? h('p', { className: 'note err' }, s.error) : null,
      s.list === null ? h('div', { className: 'skel' }, h('span', null), h('span', null), h('span', { className: 'short' })) : null,
      own.length ? h('section', null, h('h4', { className: 'sec' }, 'Yours'),
        own.map((w) => h(Card, { key: w.id, ws: w, current: s.current, onOpen: (id) => this.open(id), onChange: (ws) => this.update(ws),
          onDeleted: (id) => this.setState((st) => ({ list: st.list.filter((x) => x.id !== id) })) }))) : null,
      shared.length ? h('section', null, h('h4', { className: 'sec' }, 'Shared with you'),
        shared.map((w) => h(Card, { key: w.id, ws: w, current: s.current, onOpen: (id) => this.open(id), onChange: (ws) => this.update(ws), onDeleted: () => {} }))) : null);
  }
}
